import React, { useEffect, useRef, useState } from 'react';
import AnchorLink from 'react-anchor-link-smooth-scroll';
import styled from 'styled-components';
import {
  email, githubLink, KEY_CODES, resume,
} from '../../config';
import { Container } from '../../styles/variables';
import StyledButton from '../StyledButton';
import socials from '../../socials';
import Icon from '../Icon';

const SideMail = styled.div`
  display: none;
  @media (min-width: 768px) {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    gap: 10px;
    position: absolute;
    text-align: center;
    right: 0;
    bottom: 0;

    a {
      display: flex;
      justify-content: center;
      flex-direction: column;
      gap: 30px;
      text-decoration: none;
      color: #35a16d;
      align-items: center;
      transition: all 100ms linear 0s;

      :hover {
        cursor: pointer;
        color: #59095e;
      }
    }

    h5 {
      font-family: Montserata, sans-serif;
      writing-mode: vertical-lr;
      font-size: 1rem;
      font-weight: 400;
      color: #cec6ca;
      transition: all 300ms linear 0s;

      :hover {
        transform: translateY(-5px);
        cursor: pointer;
        color: #35a16d;
      }
    }

    a::after {
      content: '';
      display: block;
      width: 3px;
      height: 100px;
      background-color: #cec6ca;
      border-radius: 2px;
      margin-top: 0;
    }
  }
`;

const Nav = styled.nav`
  display: flex;
  position: fixed;
  z-index: 3;
  width: 100%;
  background: rgba(95, 65, 106, 0.69);
  flex-direction: row;
  gap: 40px;
  justify-content: end;
  align-items: center;
  height: 60px;

  .mob-nav {
    display: flex;
    padding: 0 10px;
    width: 100%;
    justify-content: space-between;
    @media (min-width: 768px) {
      display: none;
    }
  }

  .nav-button {
    display: none;
  }

  a {
    text-decoration: none;
    color: #cec6ca;
    font-size: 1em;
    font-weight: bold;
    transition: all 100ms linear 0s;

    &:hover {
      cursor: pointer;
      color: #35a16d;
    }
  }

  .overlay a {
    color: white;
  }

  @media (min-width: 768px) {
    width: calc(100% - 180px);
    .nav-button {
      display: block;
    }
  }
`;

const NavItem = styled.div`
  display: none;
  @media (min-width: 768px) {
    font-family: Montserata, sans-serif;
    font-size: 1rem;
    display: block;
    color: #ffffff;
    transition: all 100ms linear 0s;
    &:hover,
    &:focus {
      cursor: pointer;
      color: #813eed;
      transform: scale(1.1);
    }
  }
`;

const Intro = styled.div`
  padding: 50px 20px;

  h5 {
    margin-left: 4px;
    color: rgba(240, 248, 255, 0.96);
  }

  h1 {
    font-family: Montserata, sans-serif;
    font-weight: 800;
    font-size: 4.5rem;
    margin-left: 0;
  }

  p {
    max-width: 500px;
    color: #cec6ca;
    color: rgba(240, 248, 255, 0.96);
  }

  a {
    width: fit-content;
    text-decoration: none;
    color: #813eed;
  }

  .aka {
    display: block;
    font-family: IndieFlower, sans-serif;
    font-size: 0.9rem;
    color: rgba(162, 165, 166, 0.96);
    transition: all 300ms linear 0s;
    width: fit-content;

    &:hover {
      cursor: pointer;
      color: #35a16d;
    }
  }

  .hero-button {
    margin-top: 40px;
  }

  .overlay a {
    color: white;
  }
`;

const StyledHamburgerButton = styled.button`

  max-width: 768px;
  position: relative;
  z-index: 10;
  margin-right: -15px;
  padding: 15px;
  border: 0;
  background-color: transparent;
  color: inherit;
  text-transform: none;
  transition-timing-function: linear;
  transition-duration: 0.15s;
  transition-property: opacity, filter;


  .ham-box {
    display: inline-block;
    position: relative;
    width: 24px;
    height: 24px;
  }

  .ham-box-inner {
    position: absolute;
    top: 50%;
    right: 0;
    width: 24px;
    height: 2px;
    border-radius: 3px;
    background-color: white;
    transition-duration: 0.22s;
    transition-property: transform;
    transition-delay: ${(props) => (props.menuOpen ? '0.12s' : '0s')};
    transform: rotate(${(props) => (props.menuOpen ? '225deg' : '0deg')});
    transition-timing-function: cubic-bezier(${(props) => (props.menuOpen ? '0.215, 0.61, 0.355, 1' : '0.55, 0.055, 0.675, 0.19')});

    &:before,
    &:after {
      content: '';
      display: block;
      position: absolute;
      left: auto;
      right: 0;
      width: 24px;
      height: 2px;
      border-radius: 4px;
      background-color: white;
      transition-timing-function: ease;
      transition-duration: 0.15s;
      transition-property: transform;
    }

    &:before {
      width: ${(props) => (props.menuOpen ? '100%' : '120%')};
      top: ${(props) => (props.menuOpen ? '0' : '-10px')};
      opacity: ${(props) => (props.menuOpen ? 0 : 1)};
      transition: ${({ menuOpen }) => (menuOpen ? '0.15s' : '0.15s')};
    }

    &:after {
      width: ${(props) => (props.menuOpen ? '100%' : '80%')};
      bottom: ${(props) => (props.menuOpen ? '0' : '-10px')};
      transform: rotate(${(props) => (props.menuOpen ? '-90deg' : '0')});
      transition: ${({ menuOpen }) => (menuOpen ? '0.15s' : '0.15s')};
    }
  }
`;

const StyledSidebar = styled.aside`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  position: fixed;
  overflow: hidden;
  top: 0;
  bottom: 0;
  right: 0;
  padding: 50px 10px;
  width: min(75vw, 400px);
  height: 100vh;
  outline: 0;
  background-color: #630752;
  box-shadow: -10px 0 30px -15px #a85698;
  z-index: 9;
  transform: translateX(${(props) => (props.menuOpen ? 0 : 100)}vw);
  visibility: ${(props) => (props.menuOpen ? 'visible' : 'hidden')};
  transition: 300ms linear 0s;
  
  nav{
    height: 200px;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    
  }
  
  .mob-nav-button {
    padding-left: 20px;
    display: flex;
    justify-content: center;
  }
  
  .mob-nav {
    display: inline-block;
    text-align: center;
  }
  
  .mob-soc{
    width: 100%;
    display: flex;
    flex-direction: row;
    justify-content: center;
    gap: 20px;
    list-style: none;
    margin-bottom: 140px;
    padding-left: 0;
  }
  
  svg {
    color: #cec6ca;
    width: 20px;
    height: 20px;
    transition: all 300ms linear 0s;
  }
`;

const one = <h5>Hi 👋🏾, my name is</h5>;
const two = (
  <h1>
    Lionel
    {' '}
    <br />
    {' '}
    Cédric KOSSI
    {' '}
    <a href={`${githubLink}`} target="_blank" rel="noreferrer">
      <span className="aka">( aka kosher )</span>
    </a>
  </h1>
);
const three = (
  <p>
    I am a full-stack developer, technical writer actually specializing in web development. I have
    a keen interest in design and I like to build things in order to satisfy
    users.
  </p>
);
const items = [
  {
    id: 0,
    itn: one,
  },
  {
    id: 1,
    itn: two,
  },
  {
    id: 2,
    itn: three,
  },
];

const navItems = [
  {
    id: 0,
    label: 'About',
    href: '#about',
  },
  {
    id: 1,
    label: 'Work',
    href: '#work',
  },
  {
    id: 2,
    label: 'Blog',
    href: '#blog',
  },
  {
    id: 3,
    label: 'Contact',
    href: '#contact',
  },
];

function Hero() {
  const [menuOpen, setMenuOpen] = useState(false);
  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
    if (!menuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'visible';
    }
  };

  const buttonRef = useRef(null);
  const navRef = useRef(null);

  let menuFocusables;
  let firstFocusableEl;
  let lastFocusableEl;

  const setFocusables = () => {
    menuFocusables = [buttonRef.current, ...Array.from(navRef.current.querySelectorAll('a'))];
    // eslint-disable-next-line prefer-destructuring
    firstFocusableEl = menuFocusables[0];
    lastFocusableEl = menuFocusables[menuFocusables.length - 1];
  };

  const handleBackwardTab = (e) => {
    if (document.activeElement === firstFocusableEl) {
      e.preventDefault();
      lastFocusableEl.focus();
    }
  };

  const handleForwardTab = (e) => {
    if (document.activeElement === lastFocusableEl) {
      e.preventDefault();
      firstFocusableEl.focus();
    }
  };

  const onKeyDown = (e) => {
    switch (e.key) {
      case KEY_CODES.ESCAPE:
      case KEY_CODES.ESCAPE_IE11: {
        setMenuOpen(false);
        break;
      }

      case KEY_CODES.TAB: {
        if (menuFocusables && menuFocusables.length === 1) {
          e.preventDefault();
          break;
        }
        if (e.shiftKey) {
          handleBackwardTab(e);
        } else {
          handleForwardTab(e);
        }
        break;
      }

      default: {
        break;
      }
    }
  };

  const onResize = (e) => {
    if (e.currentTarget.innerWidth > 768) {
      setMenuOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('resize', onResize);

    setFocusables();

    return () => {
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('resize', onResize);
    };
  }, []);

  return (
    <Container>
      <Nav>
        <div className="mob-nav">
          <h3>&lt;k/&gt;</h3>
          <StyledHamburgerButton
            onClick={toggleMenu}
            menuOpen={menuOpen}
            ref={buttonRef}
            aria-label="Menu"
          >
            <div className="ham-box">
              <div className="ham-box-inner" />
            </div>
          </StyledHamburgerButton>
          <StyledSidebar className="mob-side" menuOpen={menuOpen} aria-hidden={!menuOpen} tabIndex={menuOpen ? 1 : -1}>
            <nav ref={navRef}>
              {navItems.map((item) => (
                <NavItem className="mob-nav" key={item.id} onClick={toggleMenu}>
                  <AnchorLink offset="50" href={item.href}>{item.label}</AnchorLink>
                </NavItem>
              ))}

              <StyledButton mClassName="mob-nav-button" label="Resume" link={resume} />
            </nav>
            <div className="mob-soc-ctn">
              <ul className="mob-soc">
                {socials.map(({ url, name }) => (
                  <li key={name}>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      <Icon name={`${name}`} />
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </StyledSidebar>
        </div>
        {navItems.map((item) => (
          <NavItem key={item.id}>
            <AnchorLink offset="50" href={item.href}>{item.label}</AnchorLink>
          </NavItem>
        ))}
        <StyledButton mClassName="nav-button" label="Resume" link={resume} />
      </Nav>
      <Intro>
        {items.map((item) => (
          <div key={item.id}>{item.itn}</div>
        ))}
        <StyledButton mClassName="hero-button" label="Contact Me" link={`mailto:${email}`} />
      </Intro>
      <SideMail>
        <a href={`mailto:${email}`}>
          <h5>{email}</h5>
        </a>
      </SideMail>
    </Container>
  );
}

export default Hero;
